import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { UserRole } from "types/role"

import { useAuth } from "src/context/AuthContext"
import { RoleInfoResponse } from "src/services/api/api.types"
import { logSentryBreadcrumb } from "src/utils/crashReporting"
import { sessionScopedStorage } from "src/utils/storage/mmkv"
import { useClearConfigCache } from "./useConfig"
import { useClearCachedConversations, useClearConversationStatusFlags } from "./useConversation"
import { useClearUserCache, useDeleteUserHistory } from "./useUser"

/**
 * Storage key for the user's intended session state (sponsor + role + userId).
 * This is used to detect and recover from session drift.
 *
 * Per-tab so different tabs can be in different roles simultaneously. Backed
 * by sessionStorage on web via `sessionScopedStorage` (MMKV
 * `webStorage: "session"`).
 */
export const USER_SELECTED_SESSION_KEY = "role.user_selected_session"

export interface UserSelectedSession {
  sponsorId: string
  roleName: UserRole
  userId: string
}

/**
 * Get the locally stored intended session state.
 */
export function getUserSelectedSession(): UserSelectedSession | null {
  const sessionJson = sessionScopedStorage.getString(USER_SELECTED_SESSION_KEY)
  if (!sessionJson) return null
  try {
    return JSON.parse(sessionJson) as UserSelectedSession
  } catch (e) {
    console.error("Failed to parse user selected session from storage", e)
    return null
  }
}

/**
 * Set the locally stored intended session state.
 */
export function setUserSelectedSession(session: UserSelectedSession | null) {
  if (session) {
    sessionScopedStorage.set(USER_SELECTED_SESSION_KEY, JSON.stringify(session))
  } else {
    sessionScopedStorage.delete(USER_SELECTED_SESSION_KEY)
  }
}

/**
 * Clear the locally stored intended session state.
 */
export function clearUserSelectedSession() {
  sessionScopedStorage.delete(USER_SELECTED_SESSION_KEY)
}

/**
 * Hook to get current user role information
 *
 * Role drift detection is handled by useMemberQuery which compares user_id
 * and invalidates this query when a mismatch is detected.
 */
export function useRoleInfoQuery() {
  const { api, sponsorId } = useAuth()

  return useQuery({
    queryFn: async () => {
      const response = await api.getRoleInfo()
      if (response.kind !== "ok") {
        throw new Error(`Failed to fetch role info: ${response.kind}`)
      }
      return response.result
    },
    queryKey: ["role", "info"],
    enabled: !!api && !!sponsorId && sponsorId !== "unknown-sponsor",
  })
}

/**
 * Hook to get current user role information (with loading/error handling)
 */
export function useRoleInfo(): RoleInfoResponse | undefined {
  const roleInfoQuery = useRoleInfoQuery()

  if (roleInfoQuery.isLoading || roleInfoQuery.isError) {
    return undefined
  }

  return roleInfoQuery.data
}

/**
 * Hook to get the current user's active role
 */
export function useCurrentRole(): UserRole | undefined {
  const roleInfo = useRoleInfo()
  return roleInfo?.current_role
}

/**
 * Hook to get available roles for the current user
 */
export function useAvailableRoles() {
  const roleInfo = useRoleInfo()
  return roleInfo?.available_roles || []
}

/**
 * Hook to check if user has access to a specific role
 */
export function useHasRole(role: UserRole): boolean {
  const availableRoles = useAvailableRoles()
  return availableRoles.some((r) => r.role === role)
}

/**
 * Hook to check if user is previewing a non-default role
 */
export function useIsPreviewingRole(): boolean {
  const roleInfo = useRoleInfo()
  if (!roleInfo) return false

  return roleInfo.current_user_id !== roleInfo.main_user_id
}

/**
 * Hook to get the default role for the current user
 * Returns null when data is loading or when no default role exists
 */
export function useDefaultRole(): UserRole | null {
  const roleInfo = useRoleInfo()
  if (!roleInfo) return null // Data not loaded yet

  // Find the role that matches the main_user_id
  const defaultRoleInfo = roleInfo.available_roles.find(
    (role) => role.user_id === roleInfo.main_user_id,
  )

  // Return null if we searched but found no match
  return defaultRoleInfo ? defaultRoleInfo.role : null
}

/**
 * Mutation hook to switch user role
 */
export function useSwitchRole() {
  const { api, sponsorId } = useAuth()
  const clearRoleData = useClearRoleData()
  const clearRoleDependentData = useClearRoleDependentData()

  return useMutation({
    mutationFn: async (targetRole: UserRole): Promise<void> => {
      logSentryBreadcrumb(`role_switch_initiated:${targetRole}`)

      api.beginSessionSwitch()
      try {
        const switchResponse = await api.switchContext({
          sponsorId,
          role: targetRole,
          isLogin: false,
        })
        if (switchResponse.kind !== "ok") {
          throw new Error(`Failed to switch role: ${switchResponse.kind}`)
        }
      } finally {
        api.completeSessionSwitch()
      }

      // Query the server for the complete state after switch to establish our intended session.
      // ContextSwitchResponse is trimmed and does not carry available_roles, so getRoleInfo is still needed.
      const roleInfoResponse = await api.getRoleInfo()
      if (roleInfoResponse.kind === "ok") {
        const actualRole = roleInfoResponse.result.current_role
        const actualUserId = roleInfoResponse.result.current_user_id

        if (actualRole !== targetRole) {
          console.error(
            `[ROLE] Backend role mismatch after switchContext: expected="${targetRole}", actual="${actualRole}"`,
          )
          throw new Error(
            `Backend failed to switch to role "${targetRole}". Current role is "${actualRole}".`,
          )
        }

        setUserSelectedSession({
          sponsorId,
          roleName: actualRole,
          userId: actualUserId,
        })
        logSentryBreadcrumb(`role_switch_success:${actualRole}`)
      } else {
        console.error(
          `[ROLE] Failed to verify role switch: getRoleInfo returned ${roleInfoResponse.kind}`,
        )
        throw new Error(`Role switch verification failed: ${roleInfoResponse.kind}`)
      }

      clearRoleData()
      clearRoleDependentData()
    },
  })
}

/**
 * Mutation hook to switch to test user
 */
export function useSwitchRoleTestUser() {
  const queryClient = useQueryClient()
  const switchRoleMutation = useSwitchRole()
  const { mutateAsync: deleteUserHistoryAsync } = useDeleteUserHistory()
  const clearRoleData = useClearRoleData()
  const clearRoleDependentData = useClearRoleDependentData()

  return useMutation({
    mutationFn: async (targetRole: UserRole) => {
      try {
        await switchRoleMutation.mutateAsync(targetRole)
        console.log("useSwitchRoleTestUser: switched to test user")
      } catch (error) {
        console.error("useSwitchRoleTestUser: Failed to switch role", error)
        throw error
      }

      clearRoleData()
      clearRoleDependentData()

      try {
        await deleteUserHistoryAsync(undefined)
        console.log("useSwitchRoleTestUser: deleted user history")
      } catch (error) {
        console.error("useSwitchRoleTestUser: Failed to delete user history", error)
      }
    },
    onMutate: () => {
      // Share reset state with conversation bootstrap guards so start_conversation
      // cannot be sent while reset-demo is still in progress.
      queryClient.setQueryData(["resetDemoStatus"], { isPending: true })
    },
    onSettled: () => {
      queryClient.setQueryData(["resetDemoStatus"], { isPending: false })
    },
  })
}

/**
 * Mutation hook to reset user role to main role
 */
export function useResetRole() {
  const { api, sponsorId } = useAuth()
  const clearRoleData = useClearRoleData()
  const clearRoleDependentData = useClearRoleDependentData()

  return useMutation({
    mutationFn: async (): Promise<void> => {
      logSentryBreadcrumb("role_reset_initiated")

      api.beginSessionSwitch()
      try {
        // Omit role — backend resolves to the main/default role for the sponsor.
        const resetResponse = await api.switchContext({ sponsorId, isLogin: false })
        if (resetResponse.kind !== "ok") {
          throw new Error(`Failed to reset role: ${resetResponse.kind}`)
        }
      } finally {
        api.completeSessionSwitch()
      }

      // ContextSwitchResponse is trimmed and does not carry available_roles, so getRoleInfo is still needed.
      const roleInfoResponse = await api.getRoleInfo()
      if (roleInfoResponse.kind === "ok") {
        const actualRole = roleInfoResponse.result.current_role
        const actualUserId = roleInfoResponse.result.current_user_id
        const mainUserId = roleInfoResponse.result.main_user_id

        if (actualUserId !== mainUserId) {
          console.error(
            `[ROLE] Backend user mismatch after resetRole: expected main_user_id="${mainUserId}", actual current_user_id="${actualUserId}"`,
          )
          throw new Error(
            `Backend failed to reset to main user. Expected user_id="${mainUserId}", got "${actualUserId}".`,
          )
        }

        setUserSelectedSession({
          sponsorId,
          roleName: actualRole,
          userId: actualUserId,
        })
        logSentryBreadcrumb(`role_reset_success:${actualRole}`)
      } else {
        console.error(
          `[ROLE] Failed to verify role reset: getRoleInfo returned ${roleInfoResponse.kind}`,
        )
        throw new Error(`Role reset verification failed: ${roleInfoResponse.kind}`)
      }

      clearRoleData()
      clearRoleDependentData()
    },
  })
}

/**
 * Hook to clear role query cache only.
 * Does NOT clear expected role storage, preserving drift detection capability.
 * Used internally during role switches.
 */
export function useClearRoleData() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.removeQueries({ queryKey: ["role", "info"] })
  }
}

/**
 * Hook to clear role cache AND the user's selected session storage.
 * Use this when logging out or clearing all user data.
 */
export function useClearRoleDataAndSelectedSession() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.removeQueries({ queryKey: ["role", "info"] })
    clearUserSelectedSession()
  }
}

export function useClearRoleDependentData() {
  const clearConfigCache = useClearConfigCache()
  const clearUserCache = useClearUserCache()
  const clearCachedConversations = useClearCachedConversations()
  const clearConversationStatusFlags = useClearConversationStatusFlags()

  return () => {
    clearConfigCache()
    clearUserCache()
    clearCachedConversations()
    clearConversationStatusFlags()
  }
}
